/**
 * HealthStatusGrid - Grid layout of all service health cards
 *
 * Features:
 * - Overall status summary at top
 * - Responsive grid of service health cards
 * - Loading skeleton state
 * - Empty state handling
 */
import { cn } from '@respectly/ui';
import { Card, CardContent } from '@respectly/ui';
import { motion } from 'framer-motion';
import { ShieldCheck, AlertTriangle, XCircle, Activity } from 'lucide-react';
import { HealthStatusCard, HealthStatusCardSkeleton } from './HealthStatusCard';
import type { PlatformHealthStatus, HealthStatus } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface HealthStatusGridProps {
  healthStatus: PlatformHealthStatus | null;
  isLoading?: boolean;
  className?: string;
}

// ============================================
// Constants
// ============================================

const OVERALL_STATUS_CONFIG: Record<
  HealthStatus,
  { icon: React.ElementType; color: string; bgColor: string; label: string; description: string }
> = {
  healthy: {
    icon: ShieldCheck,
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-gradient-to-br from-emerald-500 to-green-600',
    label: 'All Systems Operational',
    description: 'All services are running normally',
  },
  degraded: {
    icon: AlertTriangle,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-gradient-to-br from-amber-500 to-orange-600',
    label: 'Degraded Performance',
    description: 'Some services are experiencing issues',
  },
  down: {
    icon: XCircle,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-gradient-to-br from-red-500 to-rose-600',
    label: 'System Outage',
    description: 'Critical services are unavailable',
  },
};

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};

// ============================================
// Component
// ============================================

export function HealthStatusGrid({
  healthStatus,
  isLoading = false,
  className,
}: HealthStatusGridProps) {
  if (isLoading) {
    return (
      <div className={cn('space-y-6', className)}>
        {/* Overall Status Skeleton */}
        <Card className="border-slate-200/60 dark:border-slate-700/60">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 animate-pulse">
              <div className="w-14 h-14 rounded-xl bg-slate-200 dark:bg-slate-700" />
              <div>
                <div className="h-6 w-48 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
                <div className="h-4 w-64 bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Cards Skeleton */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 7 }).map((_, i) => (
            <HealthStatusCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (!healthStatus) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-16 text-center', className)}>
        <div className="h-16 w-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
          <Activity className="h-8 w-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
          No Health Data
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
          Health status data is not available. Please try refreshing.
        </p>
      </div>
    );
  }

  const config = OVERALL_STATUS_CONFIG[healthStatus.overallStatus];
  const Icon = config.icon;
  const healthyCount = healthStatus.services.filter((s) => s.status === 'healthy').length;
  const degradedCount = healthStatus.services.filter((s) => s.status === 'degraded').length;
  const downCount = healthStatus.services.filter((s) => s.status === 'down').length;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className={cn('space-y-6', className)}
    >
      {/* Overall Status Banner */}
      <motion.div variants={itemVariants}>
        <Card className="relative overflow-hidden border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900">
          {/* Status accent */}
          <div
            className={cn(
              'absolute inset-y-0 left-0 w-1',
              healthStatus.overallStatus === 'healthy' && 'bg-gradient-to-b from-emerald-400 to-emerald-500',
              healthStatus.overallStatus === 'degraded' && 'bg-gradient-to-b from-amber-400 to-amber-500',
              healthStatus.overallStatus === 'down' && 'bg-gradient-to-b from-red-400 to-red-500'
            )}
          />
          <CardContent className="p-6 pl-7">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div
                  className={cn(
                    'w-14 h-14 rounded-xl flex items-center justify-center',
                    config.bgColor
                  )}
                >
                  <Icon className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                    {config.label}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {config.description}
                  </p>
                </div>
              </div>

              {/* Status Summary Pills */}
              <div className="hidden sm:flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                    {healthyCount} Healthy
                  </span>
                </div>
                {degradedCount > 0 && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 dark:bg-amber-900/20">
                    <span className="h-2 w-2 rounded-full bg-amber-500" />
                    <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
                      {degradedCount} Degraded
                    </span>
                  </div>
                )}
                {downCount > 0 && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 dark:bg-red-900/20">
                    <span className="h-2 w-2 rounded-full bg-red-500" />
                    <span className="text-sm font-medium text-red-700 dark:text-red-400">
                      {downCount} Down
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Status Summary */}
            <div className="flex sm:hidden items-center gap-3 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
                  {healthyCount}
                </span>
              </div>
              {degradedCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-amber-500" />
                  <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
                    {degradedCount}
                  </span>
                </div>
              )}
              {downCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-red-500" />
                  <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
                    {downCount}
                  </span>
                </div>
              )}
              <span className="text-xs text-slate-400 ml-auto">
                Last updated: {new Date(healthStatus.lastUpdated).toLocaleTimeString()}
              </span>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Service Health Cards Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {healthStatus.services.map((service) => (
          <motion.div key={service.service} variants={itemVariants}>
            <HealthStatusCard service={service} />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}
